import { useEffect, useRef, useState, type FormEvent, type MouseEvent } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import spinnerTitle from 'components/others/spinnerTitle';
import { useSettings } from 'context/SettingsContext';
import {
    getCurrencyI18nParams,
    isEuroMode,
} from 'utils/helpers/currency/currencyHelpers';
import { useSetRate } from './model/useSetRate';
import SolarIcon from 'components/icons/SolarIcon';

type OffRateNewRateProps = {
    setShow: (show: boolean) => void;
};

const OffRateNewRate = ({ setShow }: OffRateNewRateProps) => {
    const { t } = useTranslation(['Rate', 'common']);
    const { localSettings } = useSettings();
    const [showNewRate, setShowNewRate] = useState(false);
    const refInput = useRef<HTMLInputElement>(null);
    const { isSetRate, submitRate } = useSetRate({ setShow });
    const currencyIcon = isEuroMode() ? 'bi-currency-euro' : 'bi-currency-dollar';

    const handleSubmit = async (e: FormEvent | MouseEvent) => {
        e.preventDefault();
        if (!refInput.current?.value) return;
        await submitRate(refInput.current.value);
    };

    useEffect(() => {
        if (showNewRate) refInput.current?.focus();
    }, [showNewRate]);

    const currentRate = Number(localSettings?.RATE?.current || 0);

    return (
        <div className="px-3 pb-3 text-end">
            <p className="mb-2 fs-10 text-muted text-start">
                {t('hintUahMode', getCurrencyI18nParams())}
            </p>

            <div className="d-flex">
                <div className="w-100">
                    {showNewRate && (
                        <form onSubmit={handleSubmit}>
                            <input
                                className="form-control"
                                ref={refInput}
                                required
                                disabled={isSetRate}
                                type="number"
                                min={0}
                                step={0.01}
                                defaultValue={currentRate.toFixed(2)}
                            />
                        </form>
                    )}
                </div>
                <div style={{ width: '220px' }}>
                    {showNewRate ? (
                        <Button variant="danger" disabled={isSetRate} onClick={handleSubmit}>
                            {spinnerTitle(
                                (
                                    <>
                                        <SolarIcon name={currencyIcon} className="fs-14 me-2" />
                                        {t('setRate')}
                                    </>
                                ),
                                isSetRate,
                                t('setRate'),
                            )}
                        </Button>
                    ) : (
                        <Button
                            onClick={() => {
                                setShowNewRate(true);
                                refInput.current?.focus();
                            }}
                        >
                            <SolarIcon name={currencyIcon} className="fs-14 me-2" />
                            {t('addRate')}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OffRateNewRate;
